import React, { useState, useEffect } from 'react';
import { transportAPI } from '../../services/api';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';
import toast from 'react-hot-toast';

const TransportManagement = () => {
  const [tab, setTab] = useState('vehicles');
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('vehicle');
  const [form, setForm] = useState({});

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = () => {
    setLoading(true);
    Promise.allSettled([
      transportAPI.vehicles(),
      transportAPI.drivers(),
      transportAPI.routes(),
    ]).then(([vRes, dRes, rRes]) => {
      if (vRes.status === 'fulfilled') setVehicles(vRes.value.data.results || vRes.value.data);
      if (dRes.status === 'fulfilled') setDrivers(dRes.value.data.results || dRes.value.data);
      if (rRes.status === 'fulfilled') setRoutes(rRes.value.data.results || rRes.value.data);
    }).finally(() => setLoading(false));
  };

  const openModal = (type) => { setModalType(type); setForm({}); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (modalType === 'vehicle') await transportAPI.createVehicle(form);
      else if (modalType === 'driver') await transportAPI.createDriver(form);
      else await transportAPI.createRoute(form);
      toast.success(`${modalType} added!`);
      setShowModal(false);
      loadAll();
    } catch { toast.error('Failed to save'); }
  };

  const vehicleColumns = [
    { key: 'registration_number', label: 'Reg. Number', render: v => <strong>{v}</strong> },
    { key: 'vehicle_type', label: 'Type' },
    { key: 'capacity', label: 'Capacity', render: v => `${v} seats` },
    { key: 'make', label: 'Make/Model', render: (v, row) => `${v || ''} ${row.model || ''}`.trim() || '—' },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
  ];

  const driverColumns = [
    { key: 'name', label: 'Name', render: v => <strong>{v}</strong> },
    { key: 'phone', label: 'Phone' },
    { key: 'license_number', label: 'License No.' },
    { key: 'license_expiry', label: 'Expiry' },
    { key: 'is_active', label: 'Status', render: v => <StatusBadge status={v ? 'active' : 'inactive'} /> },
  ];

  const routeColumns = [
    { key: 'name', label: 'Route Name', render: v => <strong>{v}</strong> },
    { key: 'start_point', label: 'Start' },
    { key: 'end_point', label: 'End' },
    { key: 'vehicle_number', label: 'Vehicle', render: v => v ? <code>{v}</code> : '—' },
    { key: 'driver_name', label: 'Driver', render: v => v || '—' },
    { key: 'is_active', label: 'Status', render: v => <StatusBadge status={v ? 'active' : 'inactive'} /> },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h2>Transport Management</h2><p>Vehicles, drivers, routes and student transport</p></div>
        <div className="d-flex gap-2">
          {tab === 'vehicles' && <button className="btn btn-primary" onClick={() => openModal('vehicle')}>🚌 Add Vehicle</button>}
          {tab === 'drivers' && <button className="btn btn-primary" onClick={() => openModal('driver')}>👨‍✈️ Add Driver</button>}
          {tab === 'routes' && <button className="btn btn-primary" onClick={() => openModal('route')}>🗺️ Add Route</button>}
        </div>
      </div>

      <div className="row g-3 mb-4">
        {[
          { icon: '🚌', label: 'Total Vehicles', value: vehicles.length, color: 'blue' },
          { icon: '✅', label: 'Active Vehicles', value: vehicles.filter(v => v.status === 'active').length, color: 'green' },
          { icon: '👨‍✈️', label: 'Total Drivers', value: drivers.length, color: 'purple' },
          { icon: '🗺️', label: 'Total Routes', value: routes.length, color: 'orange' },
        ].map(s => (
          <div key={s.label} className="col-6 col-lg-3">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#f1f5f9', fontSize: 22 }}>{s.icon}</div>
              <div className="stat-info"><h3>{s.value}</h3><p>{s.label}</p></div>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex gap-2 mb-4">
        {[['vehicles','🚌 Vehicles'],['drivers','👨‍✈️ Drivers'],['routes','🗺️ Routes'],['students','🎓 Student Allocation']].map(([k,l]) => (
          <button key={k} className={`btn ${tab === k ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'vehicles' && <div className="card"><div className="card-header"><span>🚌 Vehicles ({vehicles.length})</span></div><DataTable columns={vehicleColumns} data={vehicles} loading={loading} /></div>}
      {tab === 'drivers' && <div className="card"><div className="card-header"><span>👨‍✈️ Drivers ({drivers.length})</span></div><DataTable columns={driverColumns} data={drivers} loading={loading} /></div>}
      {tab === 'routes' && <div className="card"><div className="card-header"><span>🗺️ Routes ({routes.length})</span></div><DataTable columns={routeColumns} data={routes} loading={loading} /></div>}
      {tab === 'students' && (
        <div className="card"><div className="card-body">
          <div className="empty-state"><div className="empty-icon">🎓</div><h5>Student Transport Allocation</h5><p>Manage which students are assigned to which routes.</p></div>
        </div></div>
      )}

      <Modal show={showModal} onClose={() => setShowModal(false)} title={modalType === 'vehicle' ? 'Add Vehicle' : modalType === 'driver' ? 'Add Driver' : 'Add Route'}>
        <form onSubmit={handleSave}>
          {modalType === 'vehicle' && <>
            <div className="mb-3"><label className="form-label">Registration Number *</label><input className="form-control" value={form.registration_number || ''} onChange={e => setForm({...form, registration_number: e.target.value})} required /></div>
            <div className="mb-3"><label className="form-label">Capacity *</label><input type="number" className="form-control" value={form.capacity || ''} onChange={e => setForm({...form, capacity: e.target.value})} required /></div>
            <div className="mb-3"><label className="form-label">Make</label><input className="form-control" value={form.make || ''} onChange={e => setForm({...form, make: e.target.value})} /></div>
            <div className="mb-3"><label className="form-label">Model</label><input className="form-control" value={form.model || ''} onChange={e => setForm({...form, model: e.target.value})} /></div>
          </>}
          {modalType === 'driver' && <>
            <div className="mb-3"><label className="form-label">Driver Name *</label><input className="form-control" value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} required /></div>
            <div className="mb-3"><label className="form-label">Phone *</label><input className="form-control" value={form.phone || ''} onChange={e => setForm({...form, phone: e.target.value})} required /></div>
            <div className="mb-3"><label className="form-label">License Number *</label><input className="form-control" value={form.license_number || ''} onChange={e => setForm({...form, license_number: e.target.value})} required /></div>
            <div className="mb-3"><label className="form-label">License Expiry *</label><input type="date" className="form-control" value={form.license_expiry || ''} onChange={e => setForm({...form, license_expiry: e.target.value})} required /></div>
            <div className="mb-3"><label className="form-label">Joining Date *</label><input type="date" className="form-control" value={form.joining_date || ''} onChange={e => setForm({...form, joining_date: e.target.value})} required /></div>
          </>}
          {modalType === 'route' && <>
            <div className="mb-3"><label className="form-label">Route Name *</label><input className="form-control" value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} required /></div>
            <div className="mb-3"><label className="form-label">Start Point *</label><input className="form-control" value={form.start_point || ''} onChange={e => setForm({...form, start_point: e.target.value})} required /></div>
            <div className="mb-3"><label className="form-label">End Point *</label><input className="form-control" value={form.end_point || ''} onChange={e => setForm({...form, end_point: e.target.value})} required /></div>
            <div className="mb-3"><label className="form-label">Vehicle</label>
              <select className="form-select" value={form.vehicle || ''} onChange={e => setForm({...form, vehicle: e.target.value})}>
                <option value="">Select Vehicle</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration_number}</option>)}
              </select>
            </div>
            <div className="mb-3"><label className="form-label">Driver</label>
              <select className="form-select" value={form.driver || ''} onChange={e => setForm({...form, driver: e.target.value})}>
                <option value="">Select Driver</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </>}
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">➕ Add</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
export default TransportManagement;
